import * as React from "react"
import * as d3 from "d3"
import { EXPANDED_CHART_PANEL_WIDTH_PX, fitTableMeanChartWidth, resolveChartDefaultWidth } from "./chart_size"
import type { TableRowMeanEntry } from "../../layers/categorical_layer_view"

const BAR_BLUE: [number, number, number] = [59, 116, 183]
const BAR_RED: [number, number, number] = [204, 82, 82]

const INITIAL_GROW_DURATION_S = 0.8
const INITIAL_GROW_STAGGER_S = 0.08
const SORT_REORDER_DURATION_S = 1.4

function barColor(mean: number): [number, number, number] {
    return mean < 0 ? BAR_RED : BAR_BLUE
}

function formatMeanValue(mean: number): string {
    return Number.isInteger(mean) ? String(mean) : mean.toFixed(2)
}

function estimateTextWidth(text: string, compact?: boolean): number {
    return text.length * (compact ? 5.5 : 6.5)
}

function textFitsWidth(text: string, maxWidth: number, compact?: boolean): boolean {
    return estimateTextWidth(text, compact) <= maxWidth
}

function truncateToWidth(text: string, maxWidth: number, compact?: boolean): string {
    if (textFitsWidth(text, maxWidth, compact)) return text
    let truncated = text
    while (truncated.length > 1 && !textFitsWidth(`${truncated}…`, maxWidth, compact)) {
        truncated = truncated.slice(0, -1)
    }
    return `${truncated}…`
}

function wrapLabelLines(
    label: string,
    maxWidth: number,
    compact?: boolean,
    maxLines = 2
): string[] {
    if (!label) return [""]
    if (textFitsWidth(label, maxWidth, compact)) return [label]

    const words = label.split(/\s+/).filter(Boolean)
    if (words.length > 1) {
        const lines: string[] = []
        let current = ""

        for (let i = 0; i < words.length; i++) {
            const word = words[i]
            const candidate = current ? `${current} ${word}` : word
            if (textFitsWidth(candidate, maxWidth, compact)) {
                current = candidate
                continue
            }

            if (current) lines.push(current)
            current = word

            if (lines.length >= maxLines - 1) {
                const rest = words.slice(i).join(" ")
                lines.push(textFitsWidth(rest, maxWidth, compact)
                    ? rest
                    : truncateToWidth(rest, maxWidth, compact))
                return lines.slice(0, maxLines)
            }
        }

        if (current) lines.push(current)
        if (lines.length <= maxLines) return lines
    }

    const lines: string[] = []
    let remaining = label
    while (remaining && lines.length < maxLines) {
        if (textFitsWidth(remaining, maxWidth, compact)) {
            lines.push(remaining)
            break
        }

        let fitLength = 1
        while (
            fitLength < remaining.length
            && textFitsWidth(remaining.slice(0, fitLength + 1), maxWidth, compact)
        ) {
            fitLength++
        }

        if (lines.length === maxLines - 1) {
            lines.push(truncateToWidth(remaining, maxWidth, compact))
            break
        }

        lines.push(remaining.slice(0, fitLength))
        remaining = remaining.slice(fitLength).trimStart()
    }

    return lines.length > 0 ? lines : [truncateToWidth(label, maxWidth, compact)]
}

function renderWrappedLabel(
    lines: string[],
    bandHeight: number,
    labelLineHeight: number,
    compact?: boolean
) {
    const fontSize = compact ? 8 : 10
    const anchorX = -6
    const y = bandHeight / 2

    if (lines.length === 1) {
        return (
            <text
                x={anchorX}
                y={y}
                fontSize={fontSize}
                textAnchor="end"
                dominantBaseline="middle"
                fill="black"
                style={{ cursor: "default", pointerEvents: "all" }}
            >
                {lines[0]}
            </text>
        )
    }

    const startDy = -((lines.length - 1) * labelLineHeight) / 2
    return (
        <text
            x={anchorX}
            y={y}
            fontSize={fontSize}
            textAnchor="end"
            dominantBaseline="middle"
            fill="black"
            style={{ cursor: "default", pointerEvents: "all" }}
        >
            <tspan x={anchorX} dy={startDy}>{lines[0]}</tspan>
            {lines.slice(1).map((line, lineIndex) => (
                <tspan key={lineIndex} x={anchorX} dy={labelLineHeight}>{line}</tspan>
            ))}
        </text>
    )
}

function valueLabelPosition(
    mean: number,
    isNegative: boolean,
    barX: number,
    fullBarWidth: number,
    xZero: number,
    boundsWidth: number,
    compact?: boolean,
    plotRight?: number
): { x: number; anchor: "start" | "middle" | "end" } {
    const label = formatMeanValue(mean)
    const labelWidth = estimateTextWidth(label, compact)
    const minInside = compact ? 44 : 56
    const pad = 4
    const maxX = plotRight ?? boundsWidth

    if (isNegative) {
        const insideX = barX + pad
        const outsideX = barX - 5
        if (fullBarWidth >= minInside) {
            return { x: barX + fullBarWidth - pad, anchor: "end" }
        }
        if (outsideX - labelWidth >= 0) {
            return { x: outsideX, anchor: "end" }
        }
        return { x: Math.min(maxX - pad, insideX + labelWidth), anchor: "start" }
    }

    const insideX = xZero + fullBarWidth - pad
    const outsideX = xZero + fullBarWidth + 5
    if (fullBarWidth >= minInside) {
        return { x: insideX, anchor: "end" }
    }
    if (outsideX + labelWidth <= maxX) {
        return { x: outsideX, anchor: "start" }
    }
    return { x: Math.max(pad + labelWidth, insideX), anchor: "end" }
}

function rgbColor(color: [number, number, number], hover: boolean): string {
    const [r, g, b] = hover
        ? [Math.min(255, color[0] + 30), Math.min(255, color[1] + 30), Math.min(255, color[2] + 30)]
        : color
    return `rgb(${r}, ${g}, ${b})`
}

export type TableRowMeanSort = "valueDesc" | "valueAsc" | "tableOrder"

export function sortTableRowMeanRows(
    rows: TableRowMeanEntry[],
    sortType: TableRowMeanSort
): TableRowMeanEntry[] {
    if (sortType === "tableOrder") return [...rows]
    if (sortType === "valueAsc") return [...rows].sort((a, b) => a.mean - b.mean)
    return [...rows].sort((a, b) => b.mean - a.mean)
}

export interface TableRowMeanBarsProps {
    rows: TableRowMeanEntry[]
    axisLabel: string
    svgId: string
    compact?: boolean
    panelWidth?: number
    animationResetKey: string
    enableInitialGrow?: boolean
    sortType?: TableRowMeanSort
}

export const TableRowMeanBars = ({
    rows,
    axisLabel,
    svgId,
    compact,
    panelWidth,
    animationResetKey,
    enableInitialGrow = true,
    sortType = "tableOrder",
}: TableRowMeanBarsProps) => {
    const [animationComplete, setAnimationComplete] = React.useState(!enableInitialGrow)
    const [hasCompletedInitialGrow, setHasCompletedInitialGrow] = React.useState(!enableInitialGrow)
    const [hoveredBar, setHoveredBar] = React.useState<number | null>(null)

    const data = React.useMemo(
        () => sortTableRowMeanRows(rows, sortType),
        [rows, sortType]
    )

    const chartWidth = fitTableMeanChartWidth(
        compact,
        panelWidth,
        resolveChartDefaultWidth(compact, panelWidth, 160, 260, 520)
    )
    const isExpandedPanel = !compact && (panelWidth ?? 0) >= EXPANDED_CHART_PANEL_WIDTH_PX - 80
    const rightMargin = compact ? 24 : isExpandedPanel ? 32 : 2
    const labelGap = compact ? 6 : 8
    const minPlotWidth = compact ? 80 : isExpandedPanel ? 200 : 96
    let labelWidth = compact
        ? 68
        : isExpandedPanel
            ? Math.round(Math.min(180, Math.max(120, chartWidth * 0.24)))
            : Math.round(Math.min(150, Math.max(96, chartWidth * 0.26)))
    const reservedWidth = labelGap + rightMargin
    let plotWidth = chartWidth - labelWidth - reservedWidth
    if (plotWidth < minPlotWidth) {
        labelWidth = Math.max(compact ? 48 : 80, chartWidth - reservedWidth - minPlotWidth)
        plotWidth = chartWidth - labelWidth - reservedWidth
    }
    const w = chartWidth
    const labelLineHeight = compact ? 10 : 12
    const labelLayouts = React.useMemo(
        () => data.map(entry => ({
            ...entry,
            lines: wrapLabelLines(entry.label, labelWidth - 4, compact),
        })),
        [data, labelWidth, compact]
    )
    const maxLabelLineCount = Math.max(1, ...labelLayouts.map(layout => layout.lines.length))
    const baseRowHeight = compact ? 20 : Math.round(Math.min(34, Math.max(26, plotWidth * 0.04 + 20)))
    const rowHeight = maxLabelLineCount > 1
        ? (compact
            ? 32
            : Math.round(Math.min(44, Math.max(36, baseRowHeight + labelLineHeight + 2))))
        : baseRowHeight
    const barPaddingInner = compact ? 0.1 : 0.12
    const barPaddingOuter = compact ? 0.06 : 0.08
    const [h, m] = compact
        ? [Math.max(110, data.length * rowHeight + 36), { top: 14, bottom: 28, left: labelWidth + labelGap, right: rightMargin }]
        : [Math.max(140, data.length * rowHeight + 44), { top: 20, bottom: 34, left: labelWidth + labelGap, right: rightMargin }]
    const [bounds_w, bounds_h] = [(w - m.right - m.left), (h - m.top - m.bottom)]

    React.useEffect(() => {
        if (!enableInitialGrow) {
            setAnimationComplete(true)
            setHasCompletedInitialGrow(true)
            return
        }
        setHasCompletedInitialGrow(false)
        setAnimationComplete(false)
    }, [animationResetKey, enableInitialGrow])

    React.useEffect(() => {
        if (!enableInitialGrow || hasCompletedInitialGrow) return
        const timer = setTimeout(() => {
            setAnimationComplete(true)
            setHasCompletedInitialGrow(true)
        }, 100)
        return () => clearTimeout(timer)
    }, [animationResetKey, hasCompletedInitialGrow, enableInitialGrow])

    React.useEffect(() => {
        if (!hasCompletedInitialGrow) return
        setHoveredBar(null)
    }, [animationResetKey, hasCompletedInitialGrow])

    if (data.length === 0) {
        return (
            <div className="table-mean-chart__empty">
                No row data for this table in the selected area.
            </div>
        )
    }

    const minMean = Math.min(...data.map(d => d.mean))
    const maxMean = Math.max(...data.map(d => d.mean))
    const hasNegative = minMean < 0
    const plotPadRight = compact ? 36 : isExpandedPanel ? 20 : 2
    const plotPadLeft = hasNegative ? (compact ? 10 : 14) : 0
    const plotRight = bounds_w - plotPadRight
    const axisTitleX = (plotPadLeft + plotRight) / 2

    const xScale = d3
        .scaleLinear()
        .domain(hasNegative ? [Math.min(minMean, 0), Math.max(maxMean, 0)] : [0, maxMean > 0 ? maxMean : 1])
        .range([plotPadLeft, plotRight])
        .nice()

    const xZero = xScale(0)

    const yScale = d3
        .scaleBand()
        .range([0, bounds_h])
        .domain(labelLayouts.map(d => d.label))
        .paddingInner(barPaddingInner)
        .paddingOuter(barPaddingOuter)

    const useBarTransition = hasCompletedInitialGrow && animationComplete

    const rects = labelLayouts.map((d, i) => {
        const color = barColor(d.mean)
        const y = yScale(d.label)
        if (y === undefined) return null

        const xVal = xScale(d.mean)
        const fullBarWidth = Math.abs(xVal - xZero)
        const isNegative = d.mean < 0
        const barX = isNegative ? xVal : xZero
        const hoverLabel = formatMeanValue(d.mean)
        const labelInsideBar = fullBarWidth >= (compact ? 44 : 56)
        const valueLabel = valueLabelPosition(
            d.mean,
            isNegative,
            barX,
            fullBarWidth,
            xZero,
            bounds_w,
            compact,
            plotRight
        )
        const barTransition = useBarTransition
            ? "fill 0.2s ease"
            : `transform ${INITIAL_GROW_DURATION_S}s ease-out ${i * INITIAL_GROW_STAGGER_S}s, fill 0.2s ease`
        const rowTransition = useBarTransition
            ? `transform ${SORT_REORDER_DURATION_S}s ease-in-out`
            : undefined

        return (
            <g
                key={d.label}
                style={{
                    transform: `translate(0, ${y}px)`,
                    ...(rowTransition ? { transition: rowTransition } : {}),
                }}
                onMouseEnter={() => setHoveredBar(i)}
                onMouseLeave={() => setHoveredBar(null)}
            >
                <title>{d.label}</title>
                {renderWrappedLabel(d.lines, yScale.bandwidth(), labelLineHeight, compact)}
                <rect
                    x={barX}
                    y={0}
                    width={fullBarWidth}
                    height={yScale.bandwidth()}
                    stroke="lightgrey"
                    fill={rgbColor(color, hoveredBar === i)}
                    strokeWidth={1}
                    rx={1}
                    style={
                        useBarTransition
                            ? {
                                transition: barTransition,
                                cursor: "pointer",
                            }
                            : {
                                transition: barTransition,
                                transform: animationComplete ? "scaleX(1)" : "scaleX(0)",
                                transformOrigin: isNegative ? "right center" : "left center",
                                transformBox: "fill-box" as const,
                                cursor: "pointer",
                            }
                    }
                    onMouseEnter={() => setHoveredBar(i)}
                    onMouseLeave={() => setHoveredBar(null)}
                />
                {hoveredBar === i && (useBarTransition || animationComplete) && (
                    <text
                        x={valueLabel.x}
                        y={yScale.bandwidth() / 2}
                        fontSize={compact ? 8 : 10}
                        fill={labelInsideBar ? "white" : "black"}
                        textAnchor={valueLabel.anchor}
                        dominantBaseline="middle"
                        style={{ pointerEvents: "none", fontWeight: "bold" }}
                    >
                        {hoverLabel}
                    </text>
                )}
            </g>
        )
    })

    const grid = xScale.ticks(Math.min(5, data.length + 1)).map((tick, i) => {
        const tickX = xScale(tick)
        const tickLabel = String(tick)
        const tickLabelWidth = estimateTextWidth(tickLabel, compact)
        const clampedTickX = Math.max(
            plotPadLeft + tickLabelWidth / 2,
            Math.min(plotRight - tickLabelWidth / 2, tickX)
        )
        const gridTransition = useBarTransition ? "x1 0.65s ease-out, x2 0.65s ease-out" : undefined

        return (
            <g key={i}>
                <line
                    x1={tickX}
                    x2={tickX}
                    y1={0}
                    y2={bounds_h}
                    stroke={tick === 0 && hasNegative ? "#999" : "#e0e0e0"}
                    strokeWidth={tick === 0 && hasNegative ? 1.5 : 1}
                    style={gridTransition ? { transition: gridTransition } : undefined}
                />
                <text
                    x={clampedTickX}
                    y={bounds_h + (compact ? 14 : 16)}
                    fontSize={compact ? 8 : 10}
                    textAnchor="middle"
                    fill="black"
                    style={gridTransition ? { transition: "x 0.65s ease-out" } : undefined}
                >
                    {tickLabel}
                </text>
            </g>
        )
    })

    return (
        <svg
            id={svgId}
            viewBox={`0 0 ${w} ${h}`}
            width={w}
            height={h}
            preserveAspectRatio="xMidYMid meet"
            style={{
                display: "block",
                maxWidth: "100%",
                height: "auto",
                overflow: "hidden",
            }}
        >
            <g transform={`translate(${[m.left, m.top].join(",")})`}>
                {grid}
                {rects}
                <text
                    x={axisTitleX}
                    y={bounds_h + (compact ? 24 : 28)}
                    fontSize={compact ? 10 : 12}
                    textAnchor="middle"
                    fill="black"
                >
                    {axisLabel}
                </text>
            </g>
        </svg>
    )
}
